import axios, { type AxiosRequestConfig } from 'axios'
import { environmentConfig } from './universalApi'
import { handleSessionExpired, shouldHandleUnauthorized } from '../utils/sessionExpired'
import { getAuthToken } from '../utils/authStorage'
const api = axios.create({
  baseURL: environmentConfig.apiBaseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token if available
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // Default Content-Type is application/json; strip it for FormData so the
    // browser sets multipart/form-data with the correct boundary (files + fields).
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const headers = config.headers as { delete?: (key: string) => void } & Record<string, unknown>
      if (typeof headers.delete === 'function') {
        headers.delete('Content-Type')
        headers.delete('content-type')
      } else {
        delete headers['Content-Type']
        delete headers['content-type']
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error)
    console.error('API Error Details:', {
      status: error.response?.status,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method
    })
    
    // Only auto-logout for 401 errors that are NOT profile image operations or login attempts
    // This prevents logout when deleting profile images or when login fails
    if (error.response?.status === 401) {
      const url = error.config?.url || ''
      if (shouldHandleUnauthorized(url)) {
        handleSessionExpired()
      }
    }
    
    // Add retry logic for network errors
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.warn('Request timeout, consider retrying')
    }
    
    if (!error.response) {
      // Network error - no response received
      console.error('Network error - no response received')
    }
    
    return Promise.reject(error)
  }
)

// API endpoints
export const API_ENDPOINTS = {
  // Auth
  AUTH: '/auth',
  LOGIN: '/auth/login',
  REGISTER: '/auth/register',
  ME: '/auth/me',
  LOGOUT: '/auth/logout',
  
  // Users
  USERS: '/users',
  USER_BY_ID: (id: string) => `/users/${id}`,
  
  // Tutors
  TUTORS: '/tutors/',
  TUTOR_BY_ID: (id: string) => `/tutors/${id}`,
  
  // Students
  STUDENTS: '/students',
  STUDENT_BY_ID: (id: string) => `/students/${id}`,
  
  // Bookings
  BOOKINGS: '/bookings/',
  BOOKING_BY_ID: (id: string) => `/bookings/${id}`,
  
  // Sessions
  SESSIONS: '/sessions/',
  SESSION_BY_ID: (id: string) => `/sessions/${id}`,
  UPCOMING_SESSIONS: '/sessions/upcoming',
  
  // Conversations
  CONVERSATIONS: '/conversations/',
  CONVERSATION_BY_ID: (id: string) => `/conversations/${id}/`,
  CONVERSATION_BY_PARTICIPANTS: (studentId: string, tutorId: string) => `/conversations/participants/${studentId}/${tutorId}`,
  ACTIVE_CONVERSATIONS: '/conversations/active',
  CONVERSATION_STATUS: (id: string) => `/conversations/${id}/status`,
  
  // Messages
  MESSAGES: '/messages/',
  MESSAGE_BY_ID: (id: string) => `/messages/${id}/`,
  
  // Education
  EDUCATION: '/education/',
  EDUCATION_BY_ID: (id: string) => `/education/${id}`,
  
  // Experience
  EXPERIENCE: '/experience/',
  EXPERIENCE_BY_ID: (id: string) => `/experience/${id}`,
}

// Generic API methods
export const apiService = {
  // GET request — second arg may be axios config or a plain query-params object.
  get: async <T>(url: string, configOrParams?: AxiosRequestConfig | Record<string, unknown>): Promise<T> => {
    let config: AxiosRequestConfig | undefined
    if (configOrParams) {
      const keys = Object.keys(configOrParams)
      const isAxiosConfig = keys.some((key) =>
        ['params', 'headers', 'timeout', 'responseType', 'signal', 'auth'].includes(key)
      )
      config = isAxiosConfig
        ? (configOrParams as AxiosRequestConfig)
        : { params: configOrParams }
    }
    const response = await api.get(url, config)
    return response.data
  },

  // POST request — optional Axios config (e.g. upload timeout / onUploadProgress)
  post: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    const response = await api.post(url, data, config)
    return response.data
  },

  // PUT request
  put: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    const response = await api.put(url, data, config)
    return response.data
  },

  // DELETE request
  delete: async <T>(url: string, data?: any): Promise<T> => {
    const response = await api.delete(url, { data })
    return response.data
  },

  // PATCH request
  patch: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    const response = await api.patch(url, data, config)
    return response.data
  },
}

/** Per-request timeout for multipart file uploads (keeps global JSON timeout at 30s). */
export const UPLOAD_TIMEOUT_MS = 180_000

export default api
